import { useMemo } from "react";
import { decodeTime } from "ulid";

import DocumentationButton from "components/DocumentationButton";
import { EmptystateWebhooksColored } from "components/icons/generated";
import useTableSort from "components/Table/useTableSort";
import { WebhooksTierInfo } from "components/TierInfo/WebhooksTierInfo";
import Box from "ds/components/Box";
import Button from "ds/components/Button";
import { useActiveDrawerId } from "ds/components/DrawerNew/useActiveDrawerId";
import EmptyState from "ds/components/EmptyState";
import TableSimple from "ds/components/TableSimple";
import useAnalytics from "hooks/useAnalytics";
import useTypedContext from "hooks/useTypedContext";
import { getDocsUrl } from "utils/getDocsUrl";
import Tooltip from "ds/components/Tooltip";
import { withTestId } from "utils/withTestId";

import StackSettingsFormWrapper from "../../components/FormWrapper";
import { StackSettingsContextData } from "../../Context";
import StackSettingsIntegrationsWrapper from "../components/Wrapper";
import { showStackSettingsIntegrationsWebhooksCreateFormDrawer } from "./CreateFormDrawer";
import { STACK_WEBHOOKS_TABLE_CELLS } from "./Table/Cells";
import {
  INITIAL_SORT_DIRECTION,
  INITIAL_SORT_OPTION,
  SORT_CONFIG,
  STACK_WEBHOOKS_TABLE_FILTERS_STORAGE_KEY,
} from "./Table/constants";
import { sortStackWebhooks } from "./Table/helpers";
import useStackWebhooksTableStore from "./Table/useTableStore";

const VIRTUALIZATION_THRESHOLD = 50;

const StackSettingsIntegrationsWebhooks = () => {
  const { stackSettings, canManageStack } = useTypedContext(StackSettingsContextData);
  const trackAnalytics = useAnalytics();
  const activeRowId = useActiveDrawerId();

  const { sortDescriptor } = useTableSort({
    filtersOrderSettingsKey: STACK_WEBHOOKS_TABLE_FILTERS_STORAGE_KEY,
    sortConfig: SORT_CONFIG,
    initialSortOption: INITIAL_SORT_OPTION,
    initialSortDirection: INITIAL_SORT_DIRECTION,
  });

  const webhooks = useMemo(() => {
    const items = (stackSettings.integrations?.webhooks || []).map((item) => ({
      ...item,
      createdAt: decodeTime(item.id) / 1000,
    }));

    return sortStackWebhooks(items, sortDescriptor);
  }, [stackSettings.integrations?.webhooks, sortDescriptor]);

  const handleOnCreateClick = () => {
    showStackSettingsIntegrationsWebhooksCreateFormDrawer({
      stackId: stackSettings.id,
    });
  };

  const isCreateButtonVisible = !!canManageStack.message || canManageStack.result;

  return (
    <StackSettingsIntegrationsWrapper
      actions={
        isCreateButtonVisible ? (
          <Tooltip
            active={!!canManageStack.message}
            on={(props) => (
              <Button
                {...props}
                {...withTestId("create-webhook-button")}
                variant="primary"
                disabled={!canManageStack.result}
                onPress={handleOnCreateClick}
              >
                Create webhook
              </Button>
            )}
          >
            {canManageStack.message}
          </Tooltip>
        ) : null
      }
    >
      <WebhooksTierInfo />
      <StackSettingsFormWrapper padding="0" gap="0" withLimitWidth={false}>
        {webhooks.length > 0 && (
          <TableSimple
            data={webhooks}
            store={useStackWebhooksTableStore}
            ariaLabel="Webhooks"
            trackAnalytics={trackAnalytics}
            cellPerColumn={STACK_WEBHOOKS_TABLE_CELLS}
            filtersStorageKey={STACK_WEBHOOKS_TABLE_FILTERS_STORAGE_KEY}
            sortConfig={SORT_CONFIG}
            hasPagination={false}
            virtualize={webhooks.length >= VIRTUALIZATION_THRESHOLD}
            activeRowId={activeRowId}
          />
        )}

        {webhooks.length === 0 && (
          <Box align="center" justify="center" grow="1" fullWidth>
            <EmptyState
              padding="large"
              icon={EmptystateWebhooksColored}
              title="You do not have any webhooks yet"
              caption="Set up a webhook for your current stack to send events to a http endpoint of your choice."
            >
              <Box gap="medium">
                {(canManageStack.result || !!canManageStack.message) && (
                  <Tooltip
                    active={!!canManageStack.message}
                    on={(props) => (
                      <Button
                        {...props}
                        variant="primary"
                        onPress={handleOnCreateClick}
                        disabled={!canManageStack.result}
                      >
                        Create webhook
                      </Button>
                    )}
                  >
                    {canManageStack.message}
                  </Tooltip>
                )}
                <DocumentationButton
                  to={getDocsUrl("/integrations/webhooks")}
                  label="Documentation"
                />
              </Box>
            </EmptyState>
          </Box>
        )}
      </StackSettingsFormWrapper>
    </StackSettingsIntegrationsWrapper>
  );
};

export default StackSettingsIntegrationsWebhooks;
